import { FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { reimbursementModStyles } from './ReimbursementMod.styles';
import { reimbursementDescriptionId, requiredReceiptType, ApprovalStatus } from 'app/models/Placement/PlacementMods';
import { CategoryDescriptionOptions, isCategoryDescriptionReceiptPayDefaultTrue, isTransportationCarAllowance } from './ReimbursementHelper';
import { useSelector } from 'react-redux';
import { selectIsEditExistingMod } from 'store/redux-store/placement-mods/selectors';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import * as StatusDefaults from 'app/components/Placement/NextPlacementStatus/StatusDefaults';
import { selectCurrentModDetails } from 'store/redux-store/placement-mods/selectors';

export const ReceiptsPay = ({ isExpanded, isModEdit, onDescriptionChange }: { 
                              isExpanded: boolean; 
                              isModEdit: boolean;
                              onDescriptionChange?: (isDirty: boolean) => void;
                            }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue, clearErrors, setError } = useFormContext();
  const theme = useTheme();
  const [initialState, setInitialState] = React.useState(true);
  const initialDescriptionRef = useRef<any>(null);
  const reimbursementCategoryId = useWatch<any>({ name: 'reimbursementCategoryId' });
  const handleRadioChange = e => {
    const newValue = e.target.value === 'true' || e.target.value === true ? 'true' : 'false';
    setValue(requiredReceiptType, newValue);
    clearErrors('initialReceiptsPay');
    setInitialState(false);
  };
  const { classes } = reimbursementModStyles({ isExpanded });
  const requiredReceipts: any = useWatch({ name: requiredReceiptType });
  const reimbursementDescriptionValue = useWatch<any>({ name: reimbursementDescriptionId });
  const isEditExistingMod = useSelector(selectIsEditExistingMod);
  const isEditable = () => {
    if (isModEdit) {
      return ![
        CategoryDescriptionOptions.PerDiemCar,
        CategoryDescriptionOptions.PerDiemHousingAmenities,
        CategoryDescriptionOptions.MiscellaneousCellPhoneStipend,
        CategoryDescriptionOptions.PerDiemCellPhoneStipend,
        CategoryDescriptionOptions.MiscellaneousCarAllowance,
        CategoryDescriptionOptions.MiscellaneousMealAllowance,
        CategoryDescriptionOptions.MiscellaneousShiftCompletionBonus
      ].includes(reimbursementDescriptionValue);
    }
    return false;
  };
  const placementStatus = useSelector(selectPlacementStatus);  
  const isActiveStatus = StatusDefaults.isPositiveActiveStatus(placementStatus?.currentPlacementStatus);
  const currentModDetails = useSelector(selectCurrentModDetails);
  const isApproved = currentModDetails?.approvalStatusId === ApprovalStatus.Approved;

  useEffect(() => {
    // Check if this is the first render or if the description has changed
    const isFirstRender = initialDescriptionRef.current === null;
    const hasDescriptionChanged = !isFirstRender && 
      initialDescriptionRef.current !== reimbursementDescriptionValue;
    
    // Notify parent component when description changes (but not on first render)
    if (hasDescriptionChanged) {
      onDescriptionChange?.(true);
    }

    if (isEditExistingMod) {
      if (reimbursementDescriptionValue && isModEdit && initialState && (requiredReceipts === null || 
        requiredReceipts === undefined) && isActiveStatus === true) {
          setValue(requiredReceiptType, 'false', { shouldDirty: true });
      } else if (reimbursementDescriptionValue && isModEdit) {
        if (!isCategoryDescriptionReceiptPayDefaultTrue(reimbursementDescriptionValue) 
            || (isTransportationCarAllowance(reimbursementCategoryId, reimbursementDescriptionValue) && isActiveStatus 
               && (isApproved || hasDescriptionChanged)))
          setValue(requiredReceiptType, 'false', { shouldDirty: true });
        else {
          if (requiredReceipts === null || requiredReceipts === undefined) {
            setValue(requiredReceiptType, 'true');
          }
        }
        clearErrors('initialReceiptsPay');
      }
    } else {
       if (isModEdit && isTransportationCarAllowance(reimbursementCategoryId, reimbursementDescriptionValue) &&
          isActiveStatus) {
        setValue(requiredReceiptType, 'false', { shouldDirty: true });
        clearErrors('initialReceiptsPay');
       }
       else if (reimbursementDescriptionValue && isModEdit) {
        if (!isCategoryDescriptionReceiptPayDefaultTrue(reimbursementDescriptionValue)) {
          setValue(requiredReceiptType, 'false');
        } else {
          setValue(requiredReceiptType, 'true');
        }
        clearErrors('initialReceiptsPay');
      } 
      else {
        if (isModEdit && reimbursementCategoryId && reimbursementCategoryId !== 5) {
          setError('initialReceiptsPay', {});
        }
      }
    }
    initialDescriptionRef.current = reimbursementDescriptionValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reimbursementDescriptionValue, isModEdit, isActiveStatus, isEditExistingMod, isApproved]);

  return (
    <Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name={requiredReceiptType}
          render={({ field: { onChange, ...rest } }) => {
            return (
              <FormControl disabled={!isEditable()}>
                <FormLabel style={{ color: `${theme.palette.framework.system.charcoal}` }}>
                  {t('placement.placementMods.requiredReceipts')}
                </FormLabel>
                <RadioGroup
                  row
                  value={getValues(requiredReceiptType) ?? 'false'}
                  name={requiredReceiptType}
                  onChange={e => {
                    handleRadioChange(e);
                    onChange(e);
                  }}
                >
                  <FormControlLabel
                    checked={requiredReceipts === true || requiredReceipts === 'true' ? true : false}
                    control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                    label={'Yes'}
                    value={`true`}
                    className={classes.radioLabel}
                  />
                  <FormControlLabel
                    checked={requiredReceipts === false || requiredReceipts === 'false' ? true : false}
                    control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                    label={'No'}
                    value={`false`}
                    className={classes.radioLabel}
                  />
                </RadioGroup>
              </FormControl>
            );
          }}
        />
      </Grid>
    </Grid>
  );
};